import { type Decimal, dec, toFixedTrimmed } from "./decimal";

/**
 * Spec vocabulary. Keys and units follow docs/ingredient-data.md, which records every label each source
 * uses for them. A spec is stored as the source reported it; nothing here rewrites a reported value.
 */
export const SPEC_UNITS = [
  "lovibond",
  "srm",
  "ebc",
  "lintner",
  "windisch_kolbach",
  "percent",
  "percent_of_alpha",
  "percent_of_oil",
  "percent_abv",
  "ml_per_100g",
  "ratio",
  "fahrenheit",
  "celsius",
  "g_per_bbl",
  "billion_cells_per_g",
  "billion_cells_per_bbl",
] as const;
export type SpecUnit = (typeof SPEC_UNITS)[number];

export const SPEC_UNIT_LABELS: Record<SpecUnit, string> = {
  lovibond: "°L",
  srm: "SRM",
  ebc: "EBC",
  lintner: "°Lintner",
  windisch_kolbach: "°WK",
  percent: "%",
  percent_of_alpha: "% of alpha",
  percent_of_oil: "% of oil",
  percent_abv: "% ABV",
  ml_per_100g: "mL/100g",
  ratio: "",
  fahrenheit: "°F",
  celsius: "°C",
  g_per_bbl: "g/bbl",
  billion_cells_per_g: "B cells/g",
  billion_cells_per_bbl: "B cells/bbl",
};

type PropertyGroup = "malt" | "hop" | "yeast" | "general";

interface PropertyDefinition {
  label: string;
  group: PropertyGroup;
  kind: "numeric" | "text";
  units: readonly SpecUnit[];
}

const numeric = (
  label: string,
  group: PropertyGroup,
  ...units: SpecUnit[]
): PropertyDefinition => ({
  label,
  group,
  kind: "numeric",
  units,
});
const text = (label: string, group: PropertyGroup): PropertyDefinition => ({
  label,
  group,
  kind: "text",
  units: [],
});

export const SPEC_PROPERTIES = {
  color: numeric("Color", "malt", "lovibond", "srm", "ebc"),
  diastatic_power: numeric("Diastatic power", "malt", "lintner", "windisch_kolbach"),
  extract_fine: numeric("Extract (fine)", "malt", "percent"),
  extract_coarse: numeric("Extract (coarse)", "malt", "percent"),
  moisture: numeric("Moisture", "malt", "percent"),
  protein: numeric("Protein", "malt", "percent"),
  soluble_nitrogen_ratio: numeric("Soluble nitrogen ratio", "malt", "percent"),
  friability: numeric("Friability", "malt", "percent"),
  homogeneity: numeric("Homogeneity", "malt", "percent"),
  max_in_batch: numeric("Max in batch", "malt", "percent"),
  alpha_acid: numeric("Alpha acid", "hop", "percent"),
  beta_acid: numeric("Beta acid", "hop", "percent"),
  total_oil: numeric("Total oil", "hop", "ml_per_100g"),
  cohumulone: numeric("Cohumulone", "hop", "percent_of_alpha"),
  myrcene: numeric("Myrcene", "hop", "percent_of_oil"),
  humulene: numeric("Humulene", "hop", "percent_of_oil"),
  caryophyllene: numeric("Caryophyllene", "hop", "percent_of_oil"),
  farnesene: numeric("Farnesene", "hop", "percent_of_oil"),
  linalool: numeric("Linalool", "hop", "percent_of_oil"),
  geraniol: numeric("Geraniol", "hop", "percent_of_oil"),
  b_pinene: numeric("β-Pinene", "hop", "percent_of_oil"),
  // Two keys on purpose. BeerSmith's "hsi" and the ASBC Hop Storage Index are different measurements.
  hop_storage_index: numeric("Hop storage index", "hop", "ratio"),
  alpha_loss_6mo: numeric("Alpha lost in 6 months", "hop", "percent"),
  hop_use: text("Use", "hop"),
  hop_form: text("Form", "hop"),
  attenuation: numeric("Attenuation", "yeast", "percent"),
  fermentation_temp: numeric("Fermentation temperature", "yeast", "fahrenheit", "celsius"),
  alcohol_tolerance: numeric("Alcohol tolerance", "yeast", "percent_abv"),
  pitch_rate: numeric("Pitch rate", "yeast", "g_per_bbl"),
  cell_density: numeric("Cell density", "yeast", "billion_cells_per_g", "billion_cells_per_bbl"),
  flocculation: text("Flocculation", "yeast"),
  pof: text("POF", "yeast"),
  // Whether the strain carries STA1 and can keep fermenting dextrins in the package; Omega states it
  // for every strain (Tyler, 2026-10-07).
  diastatic: text("Diastatic", "yeast"),
  strain_type: text("Type", "yeast"),
  lab_code: text("Lab code", "yeast"),
} as const satisfies Record<string, PropertyDefinition>;

export type SpecProperty = keyof typeof SPEC_PROPERTIES;

export function isSpecProperty(value: string): value is SpecProperty {
  return Object.hasOwn(SPEC_PROPERTIES, value);
}

export const QUALIFIERS = ["eq", "range", "lt", "lte", "gt", "gte"] as const;
export type Qualifier = (typeof QUALIFIERS)[number];

/**
 * A reported value. `eq` uses `min` only; `range` uses both; `lt`/`lte` use `max`; `gt`/`gte` use `min`.
 * Text specs (flocculation, hop use) carry `text` and no numbers.
 */
export type SpecValue =
  | {
      kind: "numeric";
      qualifier: Qualifier;
      min: string | null;
      max: string | null;
      unit: SpecUnit;
    }
  | { kind: "text"; text: string };

export class SpecError extends Error {}

export function validateSpec(property: SpecProperty, value: SpecValue): void {
  const definition: PropertyDefinition = SPEC_PROPERTIES[property];
  if (definition.kind !== value.kind) {
    throw new SpecError(`${definition.label} is a ${definition.kind} spec`);
  }
  if (value.kind === "text") {
    if (!value.text.trim()) throw new SpecError(`${definition.label} is empty`);
    return;
  }
  if (!definition.units.includes(value.unit)) {
    throw new SpecError(`${definition.label} cannot be measured in ${value.unit}`);
  }
  const needsMin =
    value.qualifier === "eq" || value.qualifier === "range" || value.qualifier.startsWith("g");
  const needsMax = value.qualifier === "range" || value.qualifier.startsWith("l");
  if (needsMin !== (value.min !== null) || needsMax !== (value.max !== null)) {
    throw new SpecError(`${definition.label}: "${value.qualifier}" does not fit the values given`);
  }
  if (value.min !== null) dec(value.min);
  if (value.max !== null) dec(value.max);
  if (value.qualifier === "range" && dec(value.min as string).gt(dec(value.max as string))) {
    throw new SpecError(`${definition.label}: range runs backwards`);
  }
}

/**
 * Parses a value the way supplier sheets print it: "1.3 - 1.7", "<2.0", "> 80.0", "81", "4.5-6.5%".
 * Returns null for a sheet's "not provided" marks ("-", "", "n/a").
 */
export function parseReported(raw: string, unit: SpecUnit): SpecValue | null {
  const cleaned = raw.replace(/[%°]/g, "").replace(/\s+/g, " ").trim();
  if (cleaned === "" || cleaned === "-" || /^n\/?a$/i.test(cleaned)) return null;
  const bound = /^(<=|>=|<|>|≤|≥)\s*(\d*\.?\d+)$/.exec(cleaned);
  if (bound) {
    const op = bound[1] as string;
    const number = bound[2] as string;
    const qualifier: Qualifier =
      op === "<" ? "lt" : op === ">" ? "gt" : op === "<=" || op === "≤" ? "lte" : "gte";
    const isLower = qualifier === "gt" || qualifier === "gte";
    return {
      kind: "numeric",
      qualifier,
      min: isLower ? number : null,
      max: isLower ? null : number,
      unit,
    };
  }
  const range = /^(\d*\.?\d+)\s*(?:-|to)\s*(\d*\.?\d+)$/.exec(cleaned);
  if (range) {
    return {
      kind: "numeric",
      qualifier: "range",
      min: range[1] as string,
      max: range[2] as string,
      unit,
    };
  }
  if (/^\d*\.?\d+$/.test(cleaned)) {
    return { kind: "numeric", qualifier: "eq", min: cleaned, max: null, unit };
  }
  throw new SpecError(`Cannot read "${raw}" as a value`);
}

/** The single number to use when a calculation needs one: the value, or the midpoint of a range. */
export function typicalValue(value: SpecValue): Decimal | null {
  if (value.kind !== "numeric") return null;
  if (value.qualifier === "eq") return dec(value.min as string);
  if (value.qualifier === "range")
    return dec(value.min as string)
      .plus(dec(value.max as string))
      .div("2");
  return null;
}

/**
 * The figure a recipe line starts from: the typical value, or the bound where a source gives only a
 * bound. RahrBSG prints every malt's extract as a minimum ("≥ 79.5") and CMG a few colors as a maximum
 * ("< 1.5"), and a line left blank estimates nothing (Scott, 2026-10-02). The brewer can type over it.
 */
export function startingValue(value: SpecValue): Decimal | null {
  const typical = typicalValue(value);
  if (typical !== null || value.kind !== "numeric") return typical;
  const bound = value.min ?? value.max;
  return bound === null ? null : dec(bound);
}

const COLOR_UNITS = ["lovibond", "srm", "ebc"] as const;
type ColorUnit = (typeof COLOR_UNITS)[number];

/**
 * Empirical color conversion, via SRM. EBC = 1.97 × SRM; SRM = 1.3546 × °L - 0.76. These are fitted
 * approximations, so any converted color must be shown as approximate.
 */
export function convertColor(value: string | Decimal, from: ColorUnit, to: ColorUnit): Decimal {
  const input = dec(value);
  if (from === to) return input;
  const srm =
    from === "srm"
      ? input
      : from === "ebc"
        ? input.div("1.97")
        : input.times("1.3546").minus("0.76");
  if (to === "srm") return srm;
  if (to === "ebc") return srm.times("1.97");
  return srm.plus("0.76").div("1.3546");
}

export interface DisplayedSpec {
  text: string;
  approximate: boolean;
}

/**
 * Renders a spec, converting color into the preferred unit when asked. Conversions are marked approximate;
 * values shown in the unit they were reported in are not.
 */
export function formatSpec(value: SpecValue, preferredColor?: ColorUnit): DisplayedSpec {
  if (value.kind === "text") return { text: value.text, approximate: false };
  const convert =
    preferredColor !== undefined &&
    (COLOR_UNITS as readonly string[]).includes(value.unit) &&
    value.unit !== preferredColor;
  const unit: SpecUnit = convert ? (preferredColor as SpecUnit) : value.unit;
  const show = (n: string | null) => {
    if (n === null) return "";
    const converted = convert
      ? convertColor(n, value.unit as ColorUnit, preferredColor as ColorUnit)
      : dec(n);
    return toFixedTrimmed(converted, convert ? 1 : 3);
  };
  const label = SPEC_UNIT_LABELS[unit];
  const suffix = label === "" ? "" : label.startsWith("%") ? label : ` ${label}`;
  const body = {
    eq: () => show(value.min),
    range: () => `${show(value.min)} to ${show(value.max)}`,
    lt: () => `< ${show(value.max)}`,
    lte: () => `≤ ${show(value.max)}`,
    gt: () => `> ${show(value.min)}`,
    gte: () => `≥ ${show(value.min)}`,
  }[value.qualifier]();
  return { text: `${convert ? "≈ " : ""}${body}${suffix}`, approximate: convert };
}
